import { combineEpics } from 'redux-observable'
import { Observable } from 'rxjs/Observable'

import { DEFAULT_SERVER_ERROR } from 'utils/ErrorMessages'

import { selectUserId } from 'containers/App/selectors'
import { addToast } from 'containers/Toasts/actions'

import {
  createDeepfakeKeyMemberError,
  createDeepfakeKeyMemberSuccess,
  getDeepfakeConfigError,
  getDeepfakeConfigSuccess,
  getDeepfakeKeyMembersError,
  getDeepfakeKeyMembersSuccess,
  getDeepfakeMailboxListError,
  getDeepfakeMailboxListSuccess,
  updateDeepfakeConfig,
  updateDeepfakeConfigSuccess,
  updateDeepfakeKeyMemberError,
  updateDeepfakeKeyMemberSuccess,
} from './actions'
import {
  ADD_DEEPFAKE_ALLOW_LIST_ASYNC,
  ADD_WARNING_REPORT_RECIPIENTS_ASYNC,
  CREATE_DEEPFAKE_KEY_MEMBER_ASYNC,
  DELETE_DEEPFAKE_ALLOW_LIST_ASYNC,
  DELETE_DEEPFAKE_KEY_MEMBER_ASYNC,
  DELETE_WARNING_REPORT_RECIPIENTS_ASYNC,
  GET_DEEPFAKE_ALLOW_LIST_ASYNC,
  GET_DEEPFAKE_CONFIG_ASYNC,
  GET_DEEPFAKE_KEY_MEMBERS_ASYNC,
  GET_DEEPFAKE_MAILBOX_LIST_ASYNC,
  GET_WARNING_REPORT_CANDIDATES_ASYNC,
  GET_WARNING_REPORT_RECIPIENTS_ASYNC,
  MAILBOXES_PER_PAGE,
  UPDATE_DEEPFAKE_CONFIG_ASYNC,
  UPDATE_DEEPFAKE_KEY_MEMBER_ASYNC,
} from './constants'
import {
  selectEmployeeInfo,
  selectSendWarningReportNotification,
  selectShowPopup,
} from './selectors'

const DEFAULT_SUCCESS_MESSAGE = 'Warning report was successfully updated!'

const getDeepFakeConfigEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(GET_DEEPFAKE_CONFIG_ASYNC.INITIATE).switchMap(() =>
    api.settings
      .getDeepfakeConfig()
      .map((response) => getDeepfakeConfigSuccess(response))
      .catch((error) => Observable.of(epicsErrorHandler(getDeepfakeConfigError, error)))
      .takeUntil(action$.ofType(GET_DEEPFAKE_CONFIG_ASYNC.CANCEL))
  )

const updateDeepFakeConfigEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(UPDATE_DEEPFAKE_CONFIG_ASYNC.INITIATE).switchMap((action) => {
    const body = {
      showPopup: selectShowPopup(store.getState()),
      sendWarningReportNotification: selectSendWarningReportNotification(store.getState()),
      endCall: false,
      [action.payload.key]: store.getState().settings.deepfakeGeneral.self[action.payload.key],
    }

    return api.settings
      .updateDeepfakeConfig(body)
      .map(() => updateDeepfakeConfigSuccess(action.payload.key))
      .catch((error) => Observable.of(epicsErrorHandler(updateDeepfakeConfig, error)))
      .takeUntil(action$.ofType(UPDATE_DEEPFAKE_CONFIG_ASYNC.CANCEL))
  })

const getDeepfakeMailboxListEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(GET_DEEPFAKE_MAILBOX_LIST_ASYNC.INITIATE).switchMap((action) =>
    api.mailboxes
      .get(
        action.payload.page,
        '',
        undefined,
        undefined,
        '',
        '',
        '',
        action.payload.search,
        store.getState().partnerOptions.selectedCompany?.value,
        MAILBOXES_PER_PAGE
      )
      .map((response) => getDeepfakeMailboxListSuccess(response))
      .catch((error) => Observable.of(epicsErrorHandler(getDeepfakeMailboxListError, error)))
      .takeUntil(action$.ofType(GET_DEEPFAKE_MAILBOX_LIST_ASYNC.CANCEL))
  )

const getDeepfakeKeyMembersEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(GET_DEEPFAKE_KEY_MEMBERS_ASYNC.INITIATE).switchMap(() =>
    api.settings
      .getDeepfakeKeyMembers()
      .map((response) => getDeepfakeKeyMembersSuccess(response))
      .catch((error) => Observable.of(epicsErrorHandler(getDeepfakeKeyMembersError, error)))
      .takeUntil(action$.ofType(GET_DEEPFAKE_KEY_MEMBERS_ASYNC.CANCEL))
  )

const createDeepfakeKeyMemberEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(CREATE_DEEPFAKE_KEY_MEMBER_ASYNC.INITIATE).switchMap(() => {
    const employeeInfo = selectEmployeeInfo(store.getState())

    return api.settings
      .createDeepfakeKeyMember({
        profileId: employeeInfo.profileId,
        mediaFiles: employeeInfo.mediaFiles,
      })
      .mergeMap((response) => Observable.of(createDeepfakeKeyMemberSuccess(response)))
      .catch((error) =>
        Observable.of(
          epicsErrorHandler(createDeepfakeKeyMemberError, error),
          addToast({
            message: DEFAULT_SERVER_ERROR,
            type: 'error',
          })
        )
      )
      .takeUntil(action$.ofType(CREATE_DEEPFAKE_KEY_MEMBER_ASYNC.CANCEL))
  })

const updateDeepfakeKeyMemberEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(UPDATE_DEEPFAKE_KEY_MEMBER_ASYNC.INITIATE).switchMap(() => {
    const employeeInfo = selectEmployeeInfo(store.getState())

    return api.settings
      .updateDeepfakeKeyMember(employeeInfo.id, {
        profileId: employeeInfo.profileId,
        mediaFiles: {
          imagesToCreate: employeeInfo.imagesToCreate || [],
          imagesToDelete: employeeInfo.imagesToDelete || [],
        },
      })
      .mergeMap((response) =>
        Observable.of(
          updateDeepfakeKeyMemberSuccess(response),
          addToast({ message: 'Key member was successfully updated!' })
        )
      )
      .catch((error) =>
        Observable.of(
          epicsErrorHandler(updateDeepfakeKeyMemberError, error),
          addToast({
            message: DEFAULT_SERVER_ERROR,
            type: 'error',
          })
        )
      )
      .takeUntil(action$.ofType(UPDATE_DEEPFAKE_KEY_MEMBER_ASYNC.CANCEL))
  })

const deleteDeepfakeKeyMemberEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(DELETE_DEEPFAKE_KEY_MEMBER_ASYNC.INITIATE).switchMap((action) =>
    api.settings
      .deleteDeepfakeKeyMember(action.payload.id)
      .mergeMap((response) =>
        Observable.of(
          DELETE_DEEPFAKE_KEY_MEMBER_ASYNC.onSuccess(response),
          addToast({ message: 'Key member was successfully deleted!' })
        )
      )
      .catch((error) =>
        Observable.of(
          epicsErrorHandler(DELETE_DEEPFAKE_KEY_MEMBER_ASYNC.onError, error),
          addToast({
            message: DEFAULT_SERVER_ERROR,
            type: 'error',
          })
        )
      )
      .takeUntil(action$.ofType(DELETE_DEEPFAKE_KEY_MEMBER_ASYNC.CANCEL))
  )

const getWarningReportRecipientsEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(GET_WARNING_REPORT_RECIPIENTS_ASYNC.INITIATE).switchMap(() =>
    api.settings
      .getWarningReportRecipients()
      .map((response) => GET_WARNING_REPORT_RECIPIENTS_ASYNC.onSuccess({ data: response }))
      .catch((error) =>
        Observable.of(epicsErrorHandler(GET_WARNING_REPORT_RECIPIENTS_ASYNC.onError, error))
      )
      .takeUntil(action$.ofType(GET_WARNING_REPORT_RECIPIENTS_ASYNC.CANCEL))
  )

const getWarningReportCandidatesEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(GET_WARNING_REPORT_CANDIDATES_ASYNC.INITIATE).switchMap(() =>
    api.settings
      .getWarningReportCandidates()
      .map((response) => GET_WARNING_REPORT_CANDIDATES_ASYNC.onSuccess({ data: response }))
      .catch((error) =>
        Observable.of(epicsErrorHandler(GET_WARNING_REPORT_CANDIDATES_ASYNC.onError, error))
      )
      .takeUntil(action$.ofType(GET_WARNING_REPORT_CANDIDATES_ASYNC.CANCEL))
  )

const addWarningReportRecipientsEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(ADD_WARNING_REPORT_RECIPIENTS_ASYNC.INITIATE).switchMap((action) =>
    api.settings
      .addWarningReportRecipients(action.payload.idsToCreate.map((id) => ({ userId: id })))
      .mergeMap((response) =>
        Observable.of(
          ADD_WARNING_REPORT_RECIPIENTS_ASYNC.onSuccess(response),
          addToast({ message: DEFAULT_SUCCESS_MESSAGE })
        )
      )
      .catch((error) =>
        Observable.of(
          epicsErrorHandler(ADD_WARNING_REPORT_RECIPIENTS_ASYNC.onError, error),
          addToast({
            message: DEFAULT_SERVER_ERROR,
            type: 'error',
          })
        )
      )
      .takeUntil(action$.ofType(ADD_WARNING_REPORT_RECIPIENTS_ASYNC.CANCEL))
  )

const deleteWarningReportRecipientsEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(DELETE_WARNING_REPORT_RECIPIENTS_ASYNC.INITIATE).switchMap((action) =>
    api.settings
      .deleteWarningReportRecipients({
        ids: action.payload.idsToDelete,
      })
      .mergeMap((response) =>
        Observable.of(
          DELETE_WARNING_REPORT_RECIPIENTS_ASYNC.onSuccess(response),
          addToast({ message: DEFAULT_SUCCESS_MESSAGE })
        )
      )
      .catch((error) =>
        Observable.of(
          epicsErrorHandler(DELETE_WARNING_REPORT_RECIPIENTS_ASYNC.onError, error),
          addToast({
            message: DEFAULT_SERVER_ERROR,
            type: 'error',
          })
        )
      )
      .takeUntil(action$.ofType(DELETE_WARNING_REPORT_RECIPIENTS_ASYNC.CANCEL))
  )

const getDeepFakeAllowListEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(GET_DEEPFAKE_ALLOW_LIST_ASYNC.INITIATE).switchMap(() =>
    api.settings
      .getDeepFakeAllowList()
      .map((response) => GET_DEEPFAKE_ALLOW_LIST_ASYNC.onSuccess({ data: response }))
      .catch((error) =>
        Observable.of(epicsErrorHandler(GET_DEEPFAKE_ALLOW_LIST_ASYNC.onError, error))
      )
      .takeUntil(action$.ofType(GET_DEEPFAKE_ALLOW_LIST_ASYNC.CANCEL))
  )

const addDeepFakeAllowListEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(ADD_DEEPFAKE_ALLOW_LIST_ASYNC.INITIATE).switchMap((action) =>
    api.settings
      .addDeepFakeAllowList({
        recordType: 'tenant',
        value: action.payload.item,
        createdByUserId: selectUserId(store.getState()),
        comment: null,
      })
      .mergeMap((response) =>
        Observable.of(
          ADD_DEEPFAKE_ALLOW_LIST_ASYNC.onSuccess(response),
          addToast({ message: 'Tenant Id was successfully added to the allow list!' })
        )
      )
      .catch((error) =>
        Observable.of(epicsErrorHandler(ADD_DEEPFAKE_ALLOW_LIST_ASYNC.onError, error))
      )
      .takeUntil(action$.ofType(ADD_DEEPFAKE_ALLOW_LIST_ASYNC.CANCEL))
  )

const deleteDeepFakeAllowListEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(DELETE_DEEPFAKE_ALLOW_LIST_ASYNC.INITIATE).switchMap((action) =>
    api.settings
      .deleteDeepFakeAllowList(action.payload.id)
      .mergeMap((response) =>
        Observable.of(
          DELETE_DEEPFAKE_ALLOW_LIST_ASYNC.onSuccess(response),
          addToast({ message: 'Tenant Id was successfully deleted from the allow list!' })
        )
      )
      .catch((error) =>
        Observable.of(
          epicsErrorHandler(DELETE_DEEPFAKE_ALLOW_LIST_ASYNC.onError, error),
          addToast({
            message: DEFAULT_SERVER_ERROR,
            type: 'error',
          })
        )
      )
      .takeUntil(action$.ofType(DELETE_DEEPFAKE_ALLOW_LIST_ASYNC.CANCEL))
  )

export default combineEpics(
  getDeepFakeConfigEpic,
  updateDeepFakeConfigEpic,
  getDeepfakeMailboxListEpic,
  getDeepfakeKeyMembersEpic,
  createDeepfakeKeyMemberEpic,
  updateDeepfakeKeyMemberEpic,
  deleteDeepfakeKeyMemberEpic,
  getWarningReportRecipientsEpic,
  getWarningReportCandidatesEpic,
  addWarningReportRecipientsEpic,
  deleteWarningReportRecipientsEpic,
  getDeepFakeAllowListEpic,
  addDeepFakeAllowListEpic,
  deleteDeepFakeAllowListEpic
)
